import { useState } from 'react';
import _ from 'lodash';
import { featureCollection } from '@turf/turf';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { useEvent, useToggle } from 'react-use';
import styled from 'styled-components';
import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import { MAIN_SOURCE_ID, POINT_SOURCE_ID } from './constants';
import ImportKmlButton from '../map-toolbars/buttons/ImportKmlButton';
import { getCenterPoint } from './helpers';
import { emptyFeature, fixSelfIntersections } from '../../utils/geojson';
import { captureEvent } from '../../utils/analytics';
import parsedKmlToFeatures from '../../utils/parsedKmlToFeatures';

const DROPDOWN_ID = 'portfolio-evaluator-crop-dropdown';

const ScrollableDropdownMenu = styled(DropdownMenu)`
  max-height: 400px;
  overflow-y: auto;
`;

const onParseKml = ({ parsedKml, map, setMessages, setFeatures }) => {
  if (!map) throw new Error('Map not loaded.');

  const {
    features: rawFeatures,
    info,
    warnings,
    errors,
  } = parsedKmlToFeatures(parsedKml, ['Polygon', 'MultiPolygon']);
  let features = rawFeatures.map((feature) => {
    const id = _.uniqueId('imported-feature-');
    return {
      ...feature,
      id,
      properties: {
        ...feature.properties,
        id,
      },
    };
  });

  // Only display messages if there is at least one error or warning
  if (warnings.length + errors.length) {
    setMessages({
      info,
      warning: warnings.join('\n'),
      error: errors.join('\n'),
    });
  }

  // Fix features with self-overlap
  features = features.map((f) => fixSelfIntersections(f));

  // Get 1 feature per point (center of each)
  const pointFeatures = features.map((f) => getCenterPoint(f));

  map.getSource(MAIN_SOURCE_ID).setData(featureCollection(features));
  map.getSource(POINT_SOURCE_ID).setData(featureCollection(pointFeatures));
  setFeatures(features);
};

const clear = ({ map, clearFeatures }) => {
  if (!map) throw new Error('Map not loaded.');

  map.getSource(MAIN_SOURCE_ID).setData(emptyFeature());
  map.getSource(POINT_SOURCE_ID).setData(emptyFeature());
  clearFeatures();
};

const Header = () => {
  const {
    map,
    features,
    setFeatures,
    tilesets,
    cropOptions,
    selectedCrops,
    setSelectedCrops,
    loadSuitability,
    loadingState,
    clearFeatures,
  } = usePortfolioEvaluatorContext();

  const [messages, setMessages] = useState({});
  const [cropDropdownOpen, toggle] = useToggle(false);

  // Close dropdown when clicking outside
  useEvent('click', (e) => {
    // Disregard non-relevant clicks:
    const dropdown = document.getElementById(DROPDOWN_ID);
    if (
      !dropdown || // (dropdown not loaded)
      dropdown.contains(e.target) || // (click was within dropdown)
      !cropDropdownOpen // (dropdown not currently open)
    ) {
      return;
    }

    // Close dropdown:
    toggle(false);
  });

  return (
    <div className="d-flex align-items-center">
      <ImportKmlButton
        onParse={(parsedKml) => {
          onParseKml({ parsedKml, map, setMessages, setFeatures });
          captureEvent('portfolio-evaluator-kml-imported');
        }}
        messages={messages}
        setMessages={setMessages}
        size="sm"
      >
        Import KML
      </ImportKmlButton>

      {cropOptions && (
        <Dropdown
          id={DROPDOWN_ID}
          className="ml-2"
          isOpen={cropDropdownOpen}
          toggle={toggle}
          size="sm"
        >
          <DropdownToggle>Choose crops to analyze</DropdownToggle>
          <ScrollableDropdownMenu>
            <DropdownItem
              className="font-small text-muted"
              disabled={selectedCrops.length === 0}
              onClick={() => setSelectedCrops([])}
              toggle={false} // Don't close dropdown on click
            >
              Clear all
            </DropdownItem>

            {cropOptions.map(({ label, value }) => (
              <DropdownItem
                key={value}
                className="font-small"
                active={selectedCrops.includes(value)}
                toggle={false} // Don't close dropdown on click
                onClick={() => {
                  setSelectedCrops((prev) =>
                    prev.includes(value)
                      ? prev.filter((v) => v !== value)
                      : [...prev, value],
                  );
                  captureEvent('portfolio-evaluator-crop-selected');
                }}
              >
                {label}
              </DropdownItem>
            ))}
          </ScrollableDropdownMenu>
        </Dropdown>
      )}

      {features.length > 0 && tilesets.length > 0 && (
        <Button
          className="ml-2"
          color="primary"
          size="sm"
          onClick={async () => {
            await loadSuitability();
            captureEvent('portfolio-evaluator-suitability-calculated');
          }}
          disabled={!!loadingState}
        >
          Calculate suitability
        </Button>
      )}

      {features.length > 0 && (
        <Button
          className="ml-2"
          color="link"
          size="sm"
          onClick={() => clear({ map, clearFeatures })}
        >
          Clear parcels
        </Button>
      )}
    </div>
  );
};

export default Header;
